import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaGem } from "react-icons/fa";
import "./ShieldProgressBar.css";

const SHIELD_TIERS = [
  { id: "bronze", label: "Bronze", percent: 34 },
  { id: "silver", label: "Silver", percent: 55 },
  { id: "gold", label: "Gold", percent: 74 },
  { id: "diamond", label: "Diamond", percent: 90 },
];

const TIER_STYLES = {
  bronze: {
    fill: "#CD7F32", // Metallic bronze
    stroke: "#8B4513", // Darker bronze for depth
    icon: "#FFD700", // Gold star for contrast
    accent: "#654321", // Dark bronze accent
  },
  silver: {
    fill: "#C0C0C0", // Metallic silver
    stroke: "#808080", // Darker silver for depth
    icon: "#FFFFFF", // White star for contrast
    accent: "#696969", // Dark gray accent
  },
  gold: {
    fill: "#FFD700", // Bright gold
    stroke: "#DAA520", // Darker gold for depth
    icon: "#FFFFFF", // White star for contrast
    accent: "#B8860B", // Dark gold accent
  },
  diamond: {
    fill: "#00BFFF", // Bright light blue (diamond glow)
    stroke: "#1E90FF", // Deeper blue for depth
    icon: "#FFFFFF", // White diamond for contrast
    accent: "#0066CC", // Dark blue accent
  },
};

const OFFSET = 5;
const ANIMATION_DURATION_MS = 1600;

const clampPercent = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return 0;
  }

  return Math.min(100, Math.max(0, numeric));
};

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const ShieldIcon = ({ unlocked, tier }) => {
  const theme = TIER_STYLES[tier.id] || TIER_STYLES.bronze;
  // Always show the actual tier colors, regardless of unlock status
  const fillColor = theme.fill;
  const strokeColor = theme.stroke;
  const iconColor = theme.icon;
  const accentColor = theme.accent;

  // For diamond tier, render a diamond shape instead of shield
  if (tier.id === "diamond") {
    return (
      <FaGem
        className={`shield-progress__icon ${
          unlocked ? "is-unlocked" : ""
        } is-diamond`}
        style={{
          color: "#F97316",
          width: "36px",
          height: "36px",
        }}
        aria-hidden="true"
      />
    );
  }

  const renderInnerEmblem = () => {
    return (
      <path
        d="M18 11L19.5 14.5L23 15L20 17.5L20.5 21L18 19.5L15.5 21L16 17.5L13 15L16.5 14.5L18 11Z"
        fill={iconColor}
      />
    );
  };

  return (
    <svg
      width="36"
      height="36"
      viewBox="0 0 36 36"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      className={`shield-progress__icon ${unlocked ? "is-unlocked" : ""}`}
    >
      <path
        d="M18 5L8 9V18C8 24 12 28 18 30C24 28 28 24 28 18V9L18 5Z"
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth="1.5"
      />
      {renderInnerEmblem()}
    </svg>
  );
};

const ShieldProgressBar = ({
  progress = 0,
  mastery = 60,
  nextGoal,
  className = "",
  currentPage = "",
}) => {
  const [displayProgress, setDisplayProgress] = useState(0);
  const [checkpointHeight, setCheckpointHeight] = useState(0);
  const [showRemaining, setShowRemaining] = useState(false);
  const displayValueRef = useRef(0);
  const animationFrameRef = useRef(null);
  const checkpointsRef = useRef(null);
  const targetProgress = clampPercent(progress);
  const masteryValue = clampPercent(
    typeof mastery === "number" ? mastery : targetProgress
  );

  const setAnimatedValue = (value) => {
    const clamped = clampPercent(value);
    displayValueRef.current = clamped;
    setDisplayProgress(clamped);
  };

  useEffect(() => {
    const startValue = displayValueRef.current;
    const delta = targetProgress - startValue;

    if (Math.abs(delta) < 0.1) {
      setAnimatedValue(targetProgress);
      return;
    }

    const duration = ANIMATION_DURATION_MS;
    const startTime = performance.now();

    const animate = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);
      const eased = easeOutCubic(t);
      const value = startValue + delta * eased;
      setAnimatedValue(value);

      if (t < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [targetProgress]);

  useEffect(() => {
    const measureHeight = () => {
      const nextHeight = checkpointsRef.current?.offsetHeight || 0;
      setCheckpointHeight((prev) =>
        Math.abs(prev - nextHeight) > 0.5 ? nextHeight : prev
      );
    };

    measureHeight();

    let observer;
    if (typeof ResizeObserver !== "undefined" && checkpointsRef.current) {
      observer = new ResizeObserver(measureHeight);
      observer.observe(checkpointsRef.current);
    }

    if (typeof window !== "undefined") {
      window.addEventListener("resize", measureHeight);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", measureHeight);
      }
    };
  }, []);

  const clampedProgress = displayProgress;

  const nextShield = useMemo(() => {
    return SHIELD_TIERS.find((tier) => clampedProgress < tier.percent) || null;
  }, [clampedProgress]);

  const currentShield = useMemo(() => {
    for (let i = SHIELD_TIERS.length - 1; i >= 0; i -= 1) {
      if (clampedProgress >= SHIELD_TIERS[i].percent) {
        return SHIELD_TIERS[i];
      }
    }
    return null;
  }, [clampedProgress]);

  const remainingToNext = nextShield
    ? Math.max(0, nextShield.percent - clampedProgress)
    : 0;

  const highlightWidth = nextShield ? remainingToNext : 0;
  const highlightVisible = Boolean(nextShield) && highlightWidth > 0.5;
  const shouldShowHighlight = showRemaining && highlightVisible;

  const fillBorderRadius = clampedProgress >= 100 ? "999px" : "999px 0 0 999px";
  const highlightBorderRadius = "0 999px 999px 0";

  useEffect(() => {
    setShowRemaining(false);
  }, [targetProgress]);

  useEffect(() => {
    if (!highlightVisible) {
      if (showRemaining) {
        setShowRemaining(false);
      }
      return;
    }

    const hasReachedTarget = Math.abs(displayProgress - targetProgress) < 0.5;
    if (hasReachedTarget && !showRemaining) {
      setShowRemaining(true);
    }
  }, [displayProgress, targetProgress, highlightVisible, showRemaining]);

  const goalText = currentShield
    ? `${Math.round(masteryValue)}% Mastery - Current Shield: ${
        currentShield.label
      }`
    : `${Math.round(masteryValue)}% Mastery`;

  const remainingText = nextShield
    ? `${Math.ceil(remainingToNext)}% more to ${nextShield.label} Shield`
    : nextGoal
    ? `Next Goal: ${nextGoal}`
    : "Max mastery reached";

  const containerClassName = ["shield-progress", className]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={containerClassName}>
      <div className="shield-progress__header">
        {/* <span className="shield-progress__title">Chapter Mastery</span> */}
      </div>

      <div className="shield-progress__checkpoints" aria-hidden="true">
        {SHIELD_TIERS.map((tier, index) => {
          const unlocked = clampedProgress >= tier.percent;
          return (
            <div
              ref={checkpointsRef}
              key={tier.id}
              className="shield-progress__checkpoint"
              style={{ left: `${tier.percent}%` }}
            >
              {/* <span
                className="shield-progress__marker-label"
                style={{
                  color: unlocked
                    ? TIER_STYLES[tier.id]?.accent || "#6B7280"
                    : "#6B7280",
                }}
              >
                {tier.label}
              </span> */}
              <ShieldIcon unlocked={unlocked} tier={tier} />
            </div>
          );
        })}
      </div>

      <div
        className="shield-progress__bar"
        style={
          checkpointHeight
            ? { marginTop: `${checkpointHeight / 2}px` }
            : undefined
        }
      >
        <div
          className="shield-progress__fill"
          style={{
            width: `${clampedProgress == 100 ? 100 : clampedProgress}%`,
            borderRadius: fillBorderRadius,
          }}
        />
        {/* {shouldShowHighlight && (
          <div
            className="shield-progress__remaining is-visible"
            style={{
              left: `${clampedProgress - OFFSET}%`,
              width: `${highlightWidth + OFFSET}%`,
              borderRadius: highlightBorderRadius,
            }}
          />
        )} */}

        <div className="shield-progress__markers" aria-hidden="true">
          {/* {SHIELD_TIERS.map((tier) => (
            <div
              key={tier.id}
              className="shield-progress__checkpoint text-white"
              style={{ left: `${tier.percent}%` }}
            >
              <span className="shield-progress__checkpoint-value text-white">
                {tier.percent + OFFSET}%
              </span>
            </div>
          ))} */}
        </div>

        {/* Goal text overlay on progress bar */}
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            fontWeight: "600",
            fontSize: "18px",
            whiteSpace: "nowrap",
            pointerEvents: "none",
            zIndex: 10,
          }}
          className="text-black"
        >
          {goalText}
        </div>
      </div>

      <div className="shield-progress__meta">
        {/* <p className="shield-progress__meta-primary">{goalText}</p> */}
        {/* <p className="shield-progress__meta-secondary">{remainingText}</p> */}
      </div>
    </div>
  );
};

export default ShieldProgressBar;
